import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PhotoUpload } from '../components/PhotoUpload';
import { PhotoUploadEnhanced } from '../components/PhotoUploadEnhanced';
import { WordCounter } from '../components/WordCounter';
import { SubmissionPreview } from '../components/SubmissionPreview';
import { Checkbox } from '../components/ui/checkbox';
import { Leaf, Cpu, Users, Trophy, Lightbulb, Sparkles, Target, Award, ChevronRight, ChevronLeft, Upload, CheckCircle, Loader2, X, Eye, Save, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '@/data/countries';

// Categories configuration (moved outside component for use in useEffect)
const CATEGORIES = [
  {
    id: 'sustainable',
    title: 'Best Sustainable Stand',
    icon: Leaf,
    fields: [
      { name: 'sustainability_report', label: 'Sustainability Report (PDF)', type: 'file', required: false },
      { name: 'material_sourcing', label: 'Material Sourcing Details', type: 'textarea', required: true },
      { name: 'construction_methods', label: 'Construction Methods', type: 'textarea', required: true },
      { name: 'reuse_strategy', label: 'Reuse Strategy', type: 'textarea', required: true },
      { name: 'environmental_certifications', label: 'Environmental Certifications', type: 'text', required: false },
      { name: 'photos', label: 'Stand Fotoğrafları (Maksimum 5)', type: 'photos', required: false },
      { name: 'video_link', label: 'Video Link (YouTube, Vimeo, vb.)', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...' }
    ]
  },
  {
    id: 'technology',
    title: 'Best Use of Technology',
    icon: Cpu,
    fields: [
      // SECTION 1: PROJECT BASIC INFORMATION
      { name: 'section_basic', label: 'PROJECT BASIC INFORMATION', type: 'section-divider' },
      { name: 'project_name', label: 'Project Name *', type: 'text', required: true, maxLength: 100, placeholder: 'e.g., Interactive AI-Powered Product Showcase' },
      { name: 'exhibition_info', label: 'Exhibition/Event Name & Date *', type: 'text', required: true, maxLength: 150, placeholder: 'e.g., CES 2024, January 9-12' },
      { name: 'stand_area', label: 'Stand Area (m²) *', type: 'number', required: true, min: 1, placeholder: 'e.g., 200' },
      { name: 'client_brand_name', label: 'Client/Brand Name *', type: 'text', required: true, maxLength: 100, placeholder: 'e.g., Tesla Motors' },
      { name: 'budget_range', label: 'Budget Range', type: 'select', required: false, options: ['Under €50,000', '€50,000 - €100,000', '€100,000 - €250,000', '€250,000 - €500,000', '€500,000 - €1,000,000', 'Over €1,000,000', 'Prefer not to disclose'] },
      { name: 'primary_technology_category', label: 'Primary Technology Category *', type: 'select', required: true, options: ['Artificial Intelligence / Machine Learning', 'Augmented Reality (AR)', 'Virtual Reality (VR)', 'Mixed Reality (MR/XR)', 'Interactive Displays & Touch Technology', 'Projection Mapping & Holography', 'IoT & Sensor Technology', 'Robotics & Automation', 'Data Visualization & Analytics', 'Motion Tracking & Gesture Control', 'Blockchain & Web3', '5G & Edge Computing', 'Other (please specify)'] },
      { name: 'other_technology', label: 'Other Technology (if selected) *', type: 'text', required: false, maxLength: 100, conditional: { field: 'primary_technology_category', value: 'Other (please specify)' } },
      
      // SECTION 2: TECHNOLOGY OVERVIEW
      { name: 'section_tech_overview', label: 'TECHNOLOGY OVERVIEW', type: 'section-divider' },
      { name: 'technology_concept', label: 'Technology Concept & Vision *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'Describe the overall technology concept and how it enhances the visitor experience', placeholder: 'e.g., We created an AI-powered virtual assistant that guides visitors through personalized product recommendations...' },
      { name: 'technical_challenge', label: 'Technical Challenge Addressed *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'What specific challenge or problem did this technology solve?', placeholder: 'e.g., Traditional product demos failed to engage visitors for more than 30 seconds...' },
      { name: 'innovation_uniqueness', label: 'Innovation & Uniqueness *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'What makes this technology implementation unique or groundbreaking?', placeholder: 'e.g., First exhibition stand to integrate GPT-4 with real-time product inventory...' },
      
      // SECTION 3: TECHNICAL ARCHITECTURE
      { name: 'section_architecture', label: 'TECHNICAL ARCHITECTURE', type: 'section-divider' },
      { name: 'technical_architecture', label: 'Technical Architecture Overview *', type: 'textarea', required: true, maxWords: 600, rows: 8, description: 'Describe the technical architecture, system components, and how they work together', placeholder: 'Hardware:\n- 12x Intel NUC i7 edge computing nodes\n- 8x 4K interactive touchscreens (86")\n\nSoftware:\n- Unity 3D for AR experiences\n- Python FastAPI backend...' },
      { name: 'hardware_specifications', label: 'Hardware Specifications *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'List all major hardware components, devices, and equipment used', placeholder: '- Display Technology: Samsung 86" UHD touchscreens\n- Computing: NVIDIA RTX 4090 GPUs\n- Sensors: Microsoft Azure Kinect...' },
      { name: 'software_platforms', label: 'Software & Platforms *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'List all software, platforms, frameworks, and APIs used', placeholder: 'Development:\n- Unity 3D 2023.1 with AR Foundation\n- React.js frontend dashboard\n\nAI/ML:\n- OpenAI GPT-4 API...' },
      { name: 'architecture_diagram', label: 'Technical Architecture Diagram (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload system architecture diagram, flowcharts, or technical schematics' },
      { name: 'integration_apis', label: 'Integration & APIs', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'Describe any third-party integrations, APIs, or data sources used', placeholder: '- Salesforce CRM API for lead capture\n- Google Analytics 4 for behavior tracking...' },
      
      // SECTION 4: USER EXPERIENCE & INTERACTION DESIGN
      { name: 'section_ux', label: 'USER EXPERIENCE & INTERACTION DESIGN', type: 'section-divider' },
      { name: 'user_journey', label: 'User Journey & Flow *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'Describe the complete user journey from entry to exit', placeholder: '1. Entry: Face detection triggers personalized welcome\n2. Registration: NFC badge tap loads visitor profile...' },
      { name: 'interaction_methods', label: 'Interaction Methods *', type: 'checkbox-group', required: true, options: [
        'Touch (multi-touch displays)',
        'Gesture control (hands-free)',
        'Voice commands / conversational AI',
        'Motion tracking (body/skeletal)',
        'Eye tracking / gaze detection',
        'Facial recognition',
        'NFC / RFID badges',
        'QR codes',
        'Mobile app integration',
        'Physical buttons/controllers',
        'Brain-computer interface',
        'Other'
      ], description: 'Select all interaction methods used' },
      { name: 'interaction_details', label: 'Interaction Details *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'Explain how visitors interact with the technology', placeholder: 'Visitors interact through multiple touchpoints:\n- Natural gestures detected by Azure Kinect...' },
      { name: 'ux_flow_diagrams', label: 'UX Flow Diagrams / Wireframes (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload user flow diagrams, wireframes, or UX documentation' },
      { name: 'accessibility_features', label: 'Accessibility Features', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'Describe accessibility features and inclusive design considerations', placeholder: '- Screen reader compatible interfaces\n- Wheelchair-accessible interaction heights...' },
      
      // SECTION 5: TECHNICAL IMPLEMENTATION & CHALLENGES
      { name: 'section_implementation', label: 'TECHNICAL IMPLEMENTATION & CHALLENGES', type: 'section-divider' },
      { name: 'development_process', label: 'Development Process & Timeline *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'Describe the development process, timeline, and key milestones', placeholder: 'Week 1-4: Concept & prototyping\nWeek 5-8: Software development & testing...' },
      { name: 'technical_challenges', label: 'Technical Challenges & Solutions *', type: 'textarea', required: true, maxWords: 600, rows: 8, description: 'What were the major technical challenges and how did you solve them?', placeholder: 'Challenge 1: Real-time Performance\nProblem: AR rendering dropped below 60fps...\nSolution: Implemented GPU instancing...' },
      { name: 'performance_optimization', label: 'Performance Optimization', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'How did you optimize for performance, reliability, and scalability?', placeholder: '- Load balancing across 8 compute nodes\n- Redis caching reduced database queries by 80%...' },
      { name: 'security_privacy', label: 'Security & Privacy Measures', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'What security and privacy measures were implemented?', placeholder: '- End-to-end encryption for all data transmission\n- GDPR-compliant data collection...' },
      
      // SECTION 6: MEASURABLE RESULTS & ANALYTICS
      { name: 'section_results', label: 'MEASURABLE RESULTS & ANALYTICS', type: 'section-divider' },
      { name: 'visitor_engagement_metrics', label: 'Visitor Engagement Metrics *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'Quantify visitor engagement with specific metrics', placeholder: 'Total Interactions: 12,847\nUnique Visitors: 8,392\nAverage Dwell Time: 4 minutes 32 seconds...' },
      { name: 'technology_performance', label: 'Technology Performance Data *', type: 'textarea', required: true, maxWords: 400, rows: 5, description: 'Provide technical performance metrics and uptime statistics', placeholder: 'System Uptime: 99.7%\nAverage Response Time: 47ms\nGesture Recognition Accuracy: 94.7%...' },
      { name: 'business_impact', label: 'Business Impact & ROI', type: 'textarea', required: false, maxWords: 400, rows: 5, description: 'Quantify business outcomes, lead generation, and ROI', placeholder: 'Leads Generated: 2,847\nQualified Leads: 1,234\nOn-site Sales: €127,000...' },
      { name: 'analytics_report', label: 'Post-Event Analytics Report (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload detailed analytics report, data visualizations, or performance dashboards' },
      { name: 'heatmaps_behavior', label: 'Heatmaps & Behavior Analysis (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload heatmaps, user flow analysis, or behavior tracking visualizations' },
      
      // SECTION 7: DEMONSTRATION & VISUAL DOCUMENTATION
      { name: 'section_demo', label: 'DEMONSTRATION & VISUAL DOCUMENTATION', type: 'section-divider' },
      { name: 'demo_video_primary', label: 'Technology Demo Video (Primary) *', type: 'url', required: true, placeholder: 'https://youtube.com/watch?v=...', description: 'Main demonstration video (2-5 minutes recommended)' },
      { name: 'demo_video_short', label: 'Technology Demo Video (≤2 minutes) - Short', type: 'url', required: false, placeholder: 'https://vimeo.com/...', description: 'Optional short-form demo for quick overview' },
      { name: 'user_testing_video', label: 'User Testing / Feedback Video', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...', description: 'Video showing real user interactions and feedback' },
      { name: 'tech_setup_photos', label: 'Technology Setup & Behind-the-Scenes Photos (Maximum 8)', type: 'photos', required: false, maxFiles: 8, description: 'Photos showing technical setup, hardware installation, cable management, server racks, etc.' },
      { name: 'tech_action_photos', label: 'Technology in Action Photos (Maximum 10) *', type: 'photos', required: true, maxFiles: 10, description: 'Photos showing visitors interacting with the technology during the event (minimum 3 photos required)' },
      { name: 'ui_screenshots', label: 'UI/UX Screenshots (Maximum 8)', type: 'photos', required: false, maxFiles: 8, description: 'Screenshots of user interfaces, menus, interactive elements, and content' },
      { name: 'virtual_demo_url', label: 'Virtual Tour or Interactive Demo URL', type: 'url', required: false, placeholder: 'https://demo.yoursite.com/interactive-experience', description: 'Link to interactive demo, WebGL build, or virtual tour' },
      
      // SECTION 8: TEAM & DEVELOPMENT
      { name: 'section_team', label: 'TEAM & DEVELOPMENT', type: 'section-divider' },
      { name: 'development_team', label: 'Technology Development Team *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'List developers, engineers, and technical specialists with roles', placeholder: 'Lead Developer: Alex Chen - Unity development & AR implementation\nBackend Engineer: Sarah Johnson - API development...' },
      { name: 'tech_partners', label: 'Technology Partners & Vendors', type: 'textarea', required: false, maxWords: 400, rows: 5, description: 'List hardware vendors, software partners, cloud providers, and technical collaborators', placeholder: 'Hardware:\n- Dell Technologies - Computing infrastructure\n- Samsung - Display panels...' },
      
      // SECTION 9: SUSTAINABILITY & SCALABILITY
      { name: 'section_sustainability', label: 'SUSTAINABILITY & SCALABILITY', type: 'section-divider' },
      { name: 'energy_efficiency', label: 'Energy Efficiency & Environmental Impact', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'Describe energy consumption, optimization, and environmental considerations', placeholder: '- Idle mode reduces power consumption by 60%\n- Energy-efficient LED displays...' },
      { name: 'scalability_reusability', label: 'Scalability & Reusability', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'How can this technology be scaled or reused for future events?', placeholder: 'Modular architecture allows:\n- Scaling from 5 to 50 concurrent users...' },
      
      // SECTION 10: ADDITIONAL DOCUMENTATION
      { name: 'section_additional', label: 'ADDITIONAL DOCUMENTATION', type: 'section-divider' },
      { name: 'awards_recognition', label: 'Awards & Recognition', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'Technology-related awards, industry recognition, or media coverage', placeholder: '- Fast Company Innovation by Design Award 2024\n- Webby Awards - Best Use of AR/VR...' },
      { name: 'tech_publications', label: 'Technical Publications & Case Studies', type: 'textarea', required: false, rows: 3, description: 'Links to white papers, technical blog posts, or published case studies', placeholder: 'https://medium.com/our-tech-blog/ar-exhibition-case-study\nhttps://developers.unity.com/case-studies/our-project' },
      { name: 'future_developments', label: 'Future Developments & Roadmap', type: 'textarea', required: false, maxWords: 300, rows: 4, description: 'Planned improvements, next-generation features, or ongoing development', placeholder: 'Planned enhancements:\n- Integration with Apple Vision Pro\n- Real-time language translation (15+ languages)...' }
    ]
  },
  {
    id: 'visitor_experience',
    title: 'Best Visitor Experience',
    icon: Users,
    fields: [
      { name: 'experience_journey_map', label: 'Experience Journey Map (PDF)', type: 'file', required: false },
      { name: 'floor_plan', label: 'Floor Plan Highlighting Visitor Flow', type: 'file', required: false },
      { name: 'interaction_documentation', label: 'Interaction Zone Documentation (Photos/Videos)', type: 'file', required: false },
      { name: 'quantitative_metrics', label: 'Quantitative Metrics (Dwell Time, Conversion Rates, Satisfaction Scores)', type: 'textarea', required: false },
      { name: 'photos', label: 'Ziyaretçi Deneyimi Fotoğrafları (Maksimum 5)', type: 'photos', required: false },
      { name: 'video_link', label: 'Video Link', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...' }
    ]
  },
  {
    id: 'judges_choice',
    title: "Judge's Choice Award",
    icon: Trophy,
    fields: [
      { name: 'note', label: 'Note', type: 'info', value: 'Finalists from other categories are automatically considered for this award. No separate application required.' }
    ]
  },
  {
    id: 'brand_storytelling',
    title: 'Best Brand Experience & Storytelling',
    icon: Sparkles,
    fields: [
      { name: 'brand_guidelines', label: 'Brand Guidelines or Brand Brief (PDF)', type: 'file', required: false },
      { name: 'narrative_concept', label: 'Narrative Concept Statement', type: 'textarea', required: true },
      { name: 'walkthrough_video', label: 'Walkthrough Video or Annotated Images', type: 'file', required: false },
      { name: 'visitor_testimonials', label: 'Visitor Testimonials or Qualitative Feedback', type: 'textarea', required: false },
      { name: 'photos', label: 'Brand Experience Fotoğrafları (Maksimum 5)', type: 'photos', required: false },
      { name: 'video_link', label: 'Video Link', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...' }
    ]
  },
  {
    id: 'innovation',
    title: 'Most Innovative Stand Concept',
    icon: Lightbulb,
    fields: [
      // SECTION 1: PROJECT BASIC INFORMATION
      { name: 'section_basic', label: 'PROJECT BASIC INFORMATION', type: 'section-divider' },
      { name: 'project_name', label: 'Project Name *', type: 'text', required: true, maxLength: 100, placeholder: 'e.g., Quantum Flux - Samsung Galaxy Experience' },
      { name: 'exhibition_info', label: 'Exhibition/Event Name & Date *', type: 'text', required: true, maxLength: 150, placeholder: 'e.g., IFA Berlin 2024, September 6-10' },
      { name: 'stand_area', label: 'Stand Area (m²) *', type: 'number', required: true, min: 1, placeholder: 'e.g., 450' },
      { name: 'client_brand_name', label: 'Client/Brand Name *', type: 'text', required: true, maxLength: 100, placeholder: 'e.g., Samsung Electronics' },
      { name: 'budget_range', label: 'Budget Range', type: 'select', required: false, options: ['Under €50,000', '€50,000 - €100,000', '€100,000 - €250,000', '€250,000 - €500,000', '€500,000 - €1,000,000', 'Over €1,000,000', 'Prefer not to disclose'] },
      
      // SECTION 2: INNOVATION DETAILS
      { name: 'section_innovation', label: 'INNOVATION DETAILS', type: 'section-divider' },
      { name: 'innovation_description', label: 'Innovation Description (What\'s new? Why does it matter?) *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'Describe what makes this stand concept innovative' },
      { name: 'problem_statement', label: 'Problem Statement *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'What problem or challenge did this innovation solve?', placeholder: 'e.g., Traditional exhibition stands fail to demonstrate ecosystem integration...' },
      { name: 'tech_materials_methods', label: 'Technology/Materials/Methods Used *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'What specific technology, material, or method made this stand innovative?', placeholder: 'e.g., 2,000+ individually addressable LED nodes, bio-based acrylic panels...' },
      { name: 'comparison_traditional', label: 'Comparison with Traditional Approaches *', type: 'textarea', required: true, maxWords: 300, rows: 4, description: 'How does this differ from conventional stand design approaches?', placeholder: 'e.g., Unlike traditional static displays, our stand uses responsive technology...' },
      
      // SECTION 3: TECHNICAL DOCUMENTATION
      { name: 'section_technical', label: 'TECHNICAL DOCUMENTATION', type: 'section-divider' },
      { name: 'technical_documentation', label: 'Technical Documentation (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload technical documents, specifications, or drawings' },
      { name: 'prototypes', label: 'Prototypes or Case Study Data (Maximum 5)', type: 'photos', required: false, maxFiles: 5, acceptPDF: true, description: 'Upload prototype images, case study files, or test results' },
      { name: 'custom_fabrication', label: 'Custom Fabrication & Production Details', type: 'textarea', required: false, maxWords: 500, rows: 6, description: 'Describe any custom-made elements, special fabrication techniques, or unique production methods', placeholder: 'e.g., The Quantum Core sculpture was CNC-machined from translucent acrylic...' },
      { name: 'technical_challenges', label: 'Technical Challenges & Solutions *', type: 'textarea', required: true, maxWords: 600, rows: 8, description: 'What technical challenges did you face and how did you solve them?', placeholder: 'Challenge 1: Real-time Responsiveness at Scale\\nProblem: ...\\nSolution: ...' },
      
      // SECTION 4: MEASURABLE RESULTS & IMPACT
      { name: 'section_results', label: 'MEASURABLE RESULTS & IMPACT', type: 'section-divider' },
      { name: 'impact_metrics', label: 'Impact Metrics (Cost Savings, Sustainability Metrics)', type: 'textarea', required: false, maxWords: 300, rows: 4 },
      { name: 'visitor_metrics', label: 'Visitor Engagement Metrics', type: 'textarea', required: false, maxWords: 400, rows: 5, description: 'Visitor count, engagement rate, dwell time, interaction data, etc.', placeholder: 'e.g., 47,832 total visitors, 11.5 min average dwell time, 32,147 interactions logged' },
      { name: 'roi_business_impact', label: 'ROI & Business Impact', type: 'textarea', required: false, maxWords: 400, rows: 5, description: 'Lead generation, sales impact, brand awareness metrics, conversion rates', placeholder: 'e.g., 9,847 qualified leads, 2,134 product demo requests, 2.8M social impressions' },
      { name: 'client_feedback', label: 'Client Feedback & Testimonials', type: 'textarea', required: false, maxWords: 600, rows: 8, description: 'Share feedback from the client, visitors, or stakeholders (include names and titles)', placeholder: '"This is exactly what we envisioned..." - John Doe, Marketing Director' },
      
      // SECTION 5: SUSTAINABILITY & SOCIAL IMPACT
      { name: 'section_sustainability', label: 'SUSTAINABILITY & SOCIAL IMPACT', type: 'section-divider' },
      { name: 'sustainability_features', label: 'Sustainability Features', type: 'checkbox-group', required: false, options: [
        'Reusable/modular materials',
        'Energy efficient systems',
        'Carbon footprint reduction',
        'Waste reduction strategy',
        'Recycled/eco-friendly materials',
        'Circular design principles',
        'Local sourcing'
      ] },
      { name: 'sustainability_description', label: 'Sustainability Description', type: 'textarea', required: false, maxWords: 500, rows: 6, description: 'Explain your sustainability approach and measured impact', placeholder: 'e.g., Achieved 94% material efficiency through CNC nesting software. All wood components donated to maker spaces. Aluminum framework designed for 5+ reuse cycles...' },
      
      // SECTION 6: TEAM & COLLABORATIONS
      { name: 'section_team', label: 'TEAM & COLLABORATIONS', type: 'section-divider' },
      { name: 'design_team', label: 'Design Team Credits *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'List designers, creative directors, architects and their roles', placeholder: 'Creative Director: Marina Volkova - Overall concept and creative vision\\nLead Architect: Thomas Schneider - Spatial design\\nSenior Experience Designer: Yuki Tanaka - User journey mapping' },
      { name: 'production_team', label: 'Production Team Credits *', type: 'textarea', required: true, maxWords: 500, rows: 6, description: 'List production manager, fabricators, technical team and their roles', placeholder: 'Production Manager: Hans Weber - Overall project management\\nTechnical Director: Vladimir Petrov - System integration\\nFabrication Lead: Johan Svensson - Manufacturing oversight' },
      { name: 'partners_suppliers', label: 'Partners & Suppliers', type: 'textarea', required: false, maxWords: 400, rows: 5, description: 'Technology providers, material suppliers, special collaborators, subcontractors', placeholder: 'Interactive Technology: Responsive Spaces GmbH\\nMaterials: Nordic Aluminum, BioAcryl GmbH\\nProjection: Barco laser systems' },
      
      // SECTION 7: VISUAL DOCUMENTATION
      { name: 'section_visual', label: 'VISUAL DOCUMENTATION', type: 'section-divider' },
      { name: 'concept_process_photos', label: 'Concept & Process Photos (Maximum 5)', type: 'photos', maxFiles: 5, required: false, description: 'Upload sketches, mood boards, process photos, or early renderings' },
      { name: 'final_build_photography', label: 'Final Build Photography (Maximum 10) *', type: 'photos', maxFiles: 10, required: true, description: 'High-quality photos of the completed stand (minimum 1 photo required)' },
      { name: 'innovation_detail_photos', label: 'Innovation Detail Photos (Maximum 8)', type: 'photos', maxFiles: 8, required: false, description: 'Close-up photos showing innovative features and technical details' },
      { name: 'video_showcase_url', label: 'Video Showcase URL', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...', description: 'YouTube, Vimeo, or other video platform link showcasing the innovation' },
      { name: 'virtual_tour_url', label: 'Virtual Tour / 360° Experience URL', type: 'url', required: false, placeholder: 'https://my.matterport.com/show/?m=...', description: 'Link to virtual tour, 360° photos, Matterport, or interactive experience' },
      
      // SECTION 8: ADDITIONAL DOCUMENTATION
      { name: 'section_additional', label: 'ADDITIONAL DOCUMENTATION', type: 'section-divider' },
      { name: 'awards_recognition', label: 'Awards & Recognition', type: 'textarea', required: false, maxWords: 400, rows: 4, description: 'Previous awards, recognitions, or certifications received for this project', placeholder: '- iF Design Award 2024 - Gold\\n- Red Dot Award 2024 - Best of Best\\n- Featured in Archdaily Top 10 Installations' },
      { name: 'media_coverage', label: 'Media Coverage Links', type: 'textarea', required: false, rows: 4, description: 'Links to press articles, blog posts, or media mentions (one per line)', placeholder: 'https://techcrunch.com/article-about-our-stand\\nhttps://designboom.com/feature-story\\nhttps://archdaily.com/installation-review' }
    ]
  },
  {
    id: 'structural',
    title: 'Best Structural Design & Engineering',
    icon: Target,
    fields: [
      { name: 'structural_drawings', label: 'Structural Drawings (PDF)', type: 'file', required: false },
      { name: 'material_specifications', label: 'Material Specifications', type: 'textarea', required: true },
      { name: 'fabrication_documentation', label: 'Fabrication Process Documentation', type: 'file', required: false },
      { name: 'engineer_statement', label: 'Engineer\'s Statement or Certifications', type: 'file', required: false },
      { name: 'photos', label: 'Yapısal Tasarım Fotoğrafları (Maksimum 5)', type: 'photos', required: false },
      { name: 'video_link', label: 'Video Link', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...' }
    ]
  },
  {
    id: 'creative',
    title: 'Best Creative Concept',
    icon: Award,
    fields: [
      // Section 1: Project Details
      { name: 'project_name', label: 'Project Name *', type: 'text', required: true, maxLength: 100 },
      { name: 'exhibition_info', label: 'Exhibition/Event Name & Date *', type: 'text', required: true, maxLength: 150, placeholder: 'e.g., Mobile World Congress 2024, February 26 - March 1' },
      { name: 'stand_area', label: 'Stand Area (m²) *', type: 'number', required: true, min: 1 },
      { name: 'budget_range', label: 'Budget Range', type: 'select', required: false, options: ['Under €50,000', '€50,000 - €100,000', '€100,000 - €250,000', '€250,000 - €500,000', '€500,000 - €1,000,000', 'Over €1,000,000', 'Prefer not to disclose'] },
      
      // Section 2: Concept Description
      { name: 'concept_statement', label: 'Concept Statement (≤500 words) *', type: 'textarea', maxLength: 3000, required: true, rows: 6, description: 'Core design philosophy behind this concept' },
      { name: 'design_philosophy', label: 'Design Philosophy *', type: 'textarea', maxLength: 3000, required: true, rows: 6, description: 'What is the core design philosophy behind this concept?' },
      { name: 'target_audience', label: 'Target Audience *', type: 'textarea', maxLength: 1800, required: true, rows: 4, description: 'Who was the intended audience and what were their needs?' },
      { name: 'main_theme', label: 'Main Theme & Message *', type: 'textarea', maxLength: 1800, required: true, rows: 4, description: 'What was the central theme and key message of the stand?' },
      { name: 'creative_story', label: 'Creative Process Story *', type: 'textarea', maxLength: 3600, required: true, rows: 8, description: 'Describe the creative journey from initial concept to final execution' },
      
      // Section 3: Technical Information
      { name: 'materials_used', label: 'Materials Used *', type: 'textarea', maxLength: 2400, required: true, rows: 5, description: 'List and describe the key materials, finishes, and textures used', placeholder: 'e.g., Sustainable bamboo panels, LED fabric walls, recycled aluminum structures...' },
      { name: 'custom_fabrication', label: 'Custom Fabrication & Production Details *', type: 'textarea', maxLength: 3000, required: true, rows: 6, description: 'Describe any custom-made elements, special fabrication techniques, or unique production methods' },
      { name: 'technical_challenges', label: 'Technical Challenges & Solutions *', type: 'textarea', maxLength: 3000, required: true, rows: 6, description: 'What technical challenges did you face and how did you solve them?' },
      
      // Section 4: Impact & Results
      { name: 'visitor_engagement', label: 'Visitor Engagement & Numbers', type: 'textarea', maxLength: 1800, required: false, rows: 4, description: 'Total visitors, engagement metrics, interaction data, dwell time, etc.', placeholder: 'e.g., 12,000 visitors, 6 min average dwell time, 3,500 meaningful interactions...' },
      { name: 'client_feedback', label: 'Client Feedback & Testimonials', type: 'textarea', maxLength: 2400, required: false, rows: 5, description: 'Share feedback from the client, visitors, or stakeholders' },
      { name: 'awards', label: 'Awards & Recognition', type: 'textarea', maxLength: 1800, required: false, rows: 3, description: 'List any awards, honors, or industry recognition received for this project' },
      
      // Section 5: Team Information
      { name: 'design_team', label: 'Design Team *', type: 'textarea', maxLength: 2400, required: true, rows: 5, description: 'List designers, creative directors, and their roles', placeholder: 'Creative Director: John Doe\\nLead Designer: Jane Smith\\n3D Artist: Mike Johnson' },
      { name: 'production_team', label: 'Production Team *', type: 'textarea', maxLength: 2400, required: true, rows: 5, description: 'List production manager, fabricators, technical team, and their roles' },
      { name: 'project_partners', label: 'Project Partners & Collaborators', type: 'textarea', maxLength: 1800, required: false, rows: 4, description: 'Technology partners, material suppliers, special collaborators, subcontractors' },
      
      // Section 6: Visual Documentation
      { name: 'mood_boards', label: 'Mood Boards, Sketches, or Early Renderings (Maximum 5)', type: 'photos', maxFiles: 5, acceptPDF: true, required: false, description: 'Upload concept sketches, mood boards, or early design renderings' },
      { name: 'final_photography', label: 'Final Build Photography (Maximum 10)', type: 'photos', maxFiles: 10, required: false, description: 'Upload high-quality photos of the completed stand' },
      { name: 'photos', label: 'Creative Concept Photos (Maximum 5)', type: 'photos', maxFiles: 5, required: false },
      { name: 'virtual_tour_url', label: 'Virtual Tour / 360° Experience URL', type: 'url', required: false, placeholder: 'https://my.matterport.com/show/?m=...', description: 'Link to virtual tour, 360° photos, Matterport, or interactive experience' },
      { name: 'video_link', label: 'Video Showcase URL', type: 'url', required: false, placeholder: 'https://youtube.com/watch?v=...', description: 'YouTube, Vimeo, or other video platform link showcasing the concept' }
    ]
  }
];

export const ApplicationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [judgesChoiceSuccess, setJudgesChoiceSuccess] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  
  // Check if we're in edit mode
  const editId = searchParams.get('id');
  const isEditMode = !!editId;

  // Check authentication on mount
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please sign in to submit an application');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [user, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-orange-500" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated (will be handled by useEffect, but this prevents flash)
  if (!user) {
    return null;
  }
  
  const [formData, setFormData] = useState({
    // Step 1: Category Selection
    selectedCategories: [],
    
    // Step 2: Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    
    // Step 3: Category-specific fields (will be populated based on selected categories)
    categorySpecificData: {}
  });
  
  // Load user's submissions to check limits
  useEffect(() => {
    const loadUserSubmissions = async () => {
      if (user) {
        setLoadingLimits(true);
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/submissions`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            const submissions = await response.json();
            setUserSubmissions(submissions);
          }
        } catch (error) {
          console.error('Failed to load submissions:', error);
        } finally {
          setLoadingLimits(false);
        }
      }
    };
    
    loadUserSubmissions();
  }, [user]);

  // Pre-fill form with user data when user is available
  // Load submission data in edit mode
  useEffect(() => {
    const loadSubmission = async () => {
      if (isEditMode && editId && user) {
        setLoadingSubmission(true);
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/submissions/${editId}`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            const submission = await response.json();
            
            // Find category by title using CATEGORIES constant
            const category = CATEGORIES.find(c => c.title === submission.category);
            
            if (!category) {
              console.error('Category not found:', submission.category);
              toast.error('Kategori bulunamadı');
              navigate('/dashboard');
              return;
            }
            
            // Clean up category specific data
            const cleanedData = { ...submission.category_specific_data };
            
            // Filter out empty photo objects and keep only valid base64 strings
            if (cleanedData.photos && Array.isArray(cleanedData.photos)) {
              cleanedData.photos = cleanedData.photos.filter(photo => {
                // Keep only valid strings (base64) and File objects
                if (typeof photo === 'string' && photo.trim().length > 0) {
                  return true;
                }
                if (photo instanceof File) {
                  return true;
                }
                // Filter out empty objects and invalid entries
                return false;
              });
              
              console.log(`Filtered photos: ${submission.category_specific_data.photos?.length || 0} -> ${cleanedData.photos.length}`);
            }
            
            // Load form data from submission
            setFormData({
              selectedCategories: [category.id],
              firstName: submission.basic_info.first_name || '',
              lastName: submission.basic_info.last_name || '',
              email: submission.basic_info.email || '',
              phone: submission.basic_info.phone || '',
              company: submission.basic_info.company || '',
              country: submission.basic_info.country || '',
              categorySpecificData: {
                [category.id]: cleanedData
              }
            });
            
            toast.success('Başvuru yüklendi. Düzenlemeye başlayabilirsiniz.');
          } else if (response.status === 404) {
            toast.error('Başvuru bulunamadı');
            navigate('/dashboard');
          } else if (response.status === 403) {
            toast.error('Bu başvuruyu düzenleme yetkiniz yok');
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error loading submission:', error);
          toast.error('Başvuru yüklenirken hata oluştu');
        } finally {
          setLoadingSubmission(false);
        }
      }
    };
    
    loadSubmission();
  }, [isEditMode, editId, user, navigate]);
  
  // Pre-fill form with user data when user is available (only for new submissions)
  useEffect(() => {
    if (user && !isEditMode) {
      // Split user name into first and last name
      const nameParts = user.name?.trim().split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      setFormData(prev => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        country: user.country || ''
      }));
    }
  }, [user, isEditMode]);

  // Autosave to localStorage (only for new submissions)
  useEffect(() => {
    if (!isEditMode && formData.selectedCategories.length > 0) {
      const autoSaveInterval = setInterval(() => {
        setIsSaving(true);
        const saveKey = `standvision_autosave_${user?.id || 'guest'}`;
        localStorage.setItem(saveKey, JSON.stringify(formData));
        setLastSaved(new Date());
        setTimeout(() => setIsSaving(false), 500);
      }, 30000); // Save every 30 seconds

      return () => clearInterval(autoSaveInterval);
    }
  }, [formData, isEditMode, user]);

  // Restore from localStorage on mount (only for new submissions)
  useEffect(() => {
    if (!isEditMode && user) {
      const saveKey = `standvision_autosave_${user.id}`;
      const saved = localStorage.getItem(saveKey);
      if (saved) {
        try {
          const parsedData = JSON.parse(saved);
          // Only restore if it's recent (within 7 days)
          const savedDate = new Date(parsedData.lastSaved || 0);
          const daysSince = (new Date() - savedDate) / (1000 * 60 * 60 * 24);
          if (daysSince < 7 && parsedData.selectedCategories?.length > 0) {
            setFormData(parsedData);
            toast.info('Draft restored from autosave');
            setLastSaved(savedDate);
          }
        } catch (e) {
          console.error('Failed to restore autosave:', e);
        }
      }
    }
  }, [user, isEditMode]);

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? []
        : [categoryId]
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategorySpecificChange = (categoryId, field, value) => {
    setFormData(prev => ({
      ...prev,
      categorySpecificData: {
        ...prev.categorySpecificData,
        [categoryId]: {
          ...prev.categorySpecificData[categoryId],
          [field]: value
        }
      }
    }));
  };

  // Fill test data for innovation category
  const fillTestData = () => {
    const categoryId = 'innovation';
    const testData = {
      project_name: 'Quantum Flux - Samsung Galaxy Experience',
      exhibition_info: 'IFA Berlin 2024, September 6-10',
      stand_area: '450',
      client_brand_name: 'Samsung Electronics',
      budget_range: '€500,000 - €1,000,000',
      
      innovation_description: 'The Quantum Flux stand introduces a revolutionary approach to exhibition design by creating a fully responsive environment that adapts to visitor behavior in real-time. Using 2,000+ individually addressable LED nodes and bio-based materials, we created an immersive ecosystem that demonstrates Samsung\'s innovation philosophy.',
      problem_statement: 'Traditional exhibition stands fail to demonstrate ecosystem integration and fail to create memorable, data-driven experiences. Visitors often feel disconnected from brand stories and technological innovations presented in static displays.',
      tech_materials_methods: 'We utilized 2,000+ individually addressable LED nodes integrated with bio-based acrylic panels, real-time motion tracking sensors, and AI-powered content adaptation system. The structure uses modular aluminum framework designed for 5+ reuse cycles.',
      comparison_traditional: 'Unlike traditional static displays that show pre-recorded content, our stand uses responsive technology that adapts presentations based on visitor movement, dwell time, and interaction patterns. This creates unique experiences for each visitor while reducing energy consumption by 47%.',
      
      custom_fabrication: 'The Quantum Core sculpture was CNC-machined from translucent acrylic with embedded fiber optics. All LED nodes were custom-fabricated with individual addressing capabilities. The aluminum framework uses proprietary quick-connect joints for rapid assembly without tools.',
      technical_challenges: 'Challenge 1: Real-time Responsiveness at Scale\nProblem: Processing 2,000+ LED nodes with sub-100ms latency while tracking 50+ simultaneous visitors\nSolution: Implemented distributed computing architecture with edge processing nodes\n\nChallenge 2: Seamless Modular Assembly\nProblem: 450m² structure needed tool-free assembly in 48 hours\nSolution: Developed proprietary quick-connect system with integrated cable management',
      
      impact_metrics: 'Energy consumption reduced by 47% vs traditional LED displays. Material efficiency of 94% through CNC nesting software. Carbon footprint offset through renewable energy credits. 85% of materials designed for reuse.',
      visitor_metrics: '47,832 total visitors over 4 days, 11.5 min average dwell time (3x industry average), 32,147 interactions logged, 87% positive sentiment in exit surveys',
      roi_business_impact: '9,847 qualified leads generated, 2,134 product demo requests, 2.8M social media impressions, 847 press mentions, estimated ROI of 340% based on pipeline conversion',
      client_feedback: '"This is exactly what we envisioned - a stand that doesn\'t just display our products but embodies our innovation philosophy. The visitor engagement metrics exceeded all our expectations." - Maria Schmidt, VP Marketing, Samsung Electronics Europe',
      
      sustainability_features: ['Reusable/modular materials', 'Energy efficient systems', 'Carbon footprint reduction', 'Waste reduction strategy', 'Circular design principles'],
      sustainability_description: 'Achieved 94% material efficiency through CNC nesting software. All wood components donated to local maker spaces post-event. Aluminum framework designed for 5+ reuse cycles with standardized connections. LED system uses 47% less energy than traditional lighting. Packaging reduced by 60% through modular design.',
      
      design_team: 'Creative Director: Marina Volkova - Overall concept and creative vision\nLead Architect: Thomas Schneider - Spatial design and structural engineering\nSenior Experience Designer: Yuki Tanaka - User journey mapping and interaction design\nTechnical Artist: Sophie Chen - LED programming and visual content',
      production_team: 'Production Manager: Hans Weber - Overall project management and timeline coordination\nTechnical Director: Vladimir Petrov - System integration and quality control\nFabrication Lead: Johan Svensson - Manufacturing oversight and material sourcing\nInstallation Coordinator: Emma Laurent - On-site assembly and logistics',
      partners_suppliers: 'Interactive Technology: Responsive Spaces GmbH - Motion tracking and AI systems\nMaterials: Nordic Aluminum (framework), BioAcryl GmbH (sustainable plastics)\nProjection & Display: Barco laser systems, Philips LED technology\nContent Production: Studio Quantum (video), Sonic Minds (audio design)',
      
      video_showcase_url: 'https://youtube.com/watch?v=dQw4w9WgXcQ',
      virtual_tour_url: 'https://my.matterport.com/show/?m=example123',
      
      awards_recognition: '- iF Design Award 2024 - Gold\n- Red Dot Award 2024 - Best of Best\n- German Design Award 2024 - Winner\n- Featured in Archdaily Top 10 Exhibition Stands 2024',
      media_coverage: 'https://archdaily.com/quantum-flux-samsung\nhttps://designboom.com/ifa-2024-standout-stands\nhttps://dezeen.com/samsung-responsive-exhibition'
    };

    setFormData(prev => ({
      ...prev,
      categorySpecificData: {
        ...prev.categorySpecificData,
        [categoryId]: testData
      }
    }));

    toast.success('Test data filled! Note: Photo fields need manual upload.');
  };

  // Fill test data for technology category
  const fillTechnologyTestData = () => {
    const categoryId = 'technology';
    const testData = {
      project_name: 'AI-Powered Interactive Product Showcase',
      exhibition_info: 'CES 2024, January 9-12, Las Vegas',
      stand_area: '200',
      client_brand_name: 'Tesla Motors',
      budget_range: '€250,000 - €500,000',
      primary_technology_category: 'Artificial Intelligence / Machine Learning',
      
      technology_concept: 'We created an AI-powered virtual assistant that guides visitors through personalized product recommendations based on their interests, detected through computer vision and natural language processing. The system adapts in real-time, creating unique experiences for each visitor while collecting valuable engagement data.',
      technical_challenge: 'Traditional product demos failed to engage visitors for more than 30 seconds. Our solution uses gamification and AR to create 3-minute interactive experiences that demonstrate product features in memorable, hands-on ways.',
      innovation_uniqueness: 'First exhibition stand to integrate GPT-4 with real-time product inventory, creating personalized shopping experiences. Combines facial recognition for returning visitors, gesture control, and voice commands in 8 languages.',
      
      technical_architecture: 'Hardware:\n- 12x Intel NUC i7 edge computing nodes\n- 8x 4K interactive touchscreens (86")\n- 3x Kinect Azure depth sensors\n\nSoftware:\n- Unity 3D for AR experiences\n- Python FastAPI backend\n- PostgreSQL database\n- WebSocket real-time communication\n\nIntegration:\n- REST API for CRM integration\n- MQTT for IoT sensor data\n- Cloud sync with Azure',
      hardware_specifications: '- Display Technology: Samsung 86" UHD touchscreens (12ms response)\n- Computing: NVIDIA RTX 4090 GPUs for real-time rendering\n- Sensors: Microsoft Azure Kinect v2 (depth + skeletal tracking)\n- Networking: Ubiquiti UniFi 6 Pro APs, dedicated 10Gbps fiber\n- Audio: Ultrasonic directional speakers (Sennheiser AudioBeam)',
      software_platforms: 'Development:\n- Unity 3D 2023.1 with AR Foundation\n- React.js frontend dashboard\n- Node.js + Express.js backend\n\nAI/ML:\n- OpenAI GPT-4 API for chatbot\n- TensorFlow for gesture recognition\n- Azure Computer Vision for face detection\n\nInfrastructure:\n- Docker containerization\n- Kubernetes orchestration\n- Redis for caching\n- MongoDB for analytics',
      integration_apis: '- Salesforce CRM API for lead capture\n- Google Analytics 4 for behavior tracking\n- Stripe API for on-site purchases\n- WeatherAPI for contextual content\n- Social media APIs (Twitter, Instagram) for live feeds',
      
      user_journey: '1. Entry: Face detection triggers personalized welcome\n2. Registration: NFC badge tap loads visitor profile\n3. Product Discovery: AI recommends products based on behavior\n4. Interaction: Touch, gesture, and voice controls\n5. Engagement: Gamified challenges with leaderboard\n6. Exit: Email summary with personalized content',
      interaction_methods: ['Touch (multi-touch displays)', 'Gesture control (hands-free)', 'Voice commands / conversational AI', 'Motion tracking (body/skeletal)', 'Facial recognition', 'NFC / RFID badges'],
      interaction_details: 'Visitors interact through multiple touchpoints:\n- Natural gestures detected by Azure Kinect (wave to start, swipe to navigate)\n- Voice commands in 8 languages via custom NLP model\n- Touch interfaces for detailed product exploration\n- Mobile app acts as personal controller and content repository',
      accessibility_features: '- Screen reader compatible interfaces\n- Wheelchair-accessible interaction heights (90-120cm)\n- High contrast mode for visually impaired\n- Closed captions for all video content\n- Tactile feedback for touch interactions\n- Audio descriptions for visual content',
      
      development_process: 'Week 1-4: Concept & prototyping\nWeek 5-8: Software development & testing\nWeek 9-10: Hardware procurement & setup\nWeek 11-12: Integration & debugging\nWeek 13: User testing with focus groups\nWeek 14-15: Final refinements\nWeek 16: On-site setup & deployment',
      technical_challenges: 'Challenge 1: Real-time Performance\nProblem: AR rendering dropped below 60fps with 10+ simultaneous users\nSolution: Implemented GPU instancing and Level-of-Detail (LOD) system, reducing draw calls by 70%\n\nChallenge 2: Network Reliability\nProblem: Exhibition hall WiFi interference caused connection drops\nSolution: Deployed private 5GHz mesh network with automatic failover to LTE backup\n\nChallenge 3: Gesture Recognition Accuracy\nProblem: False positives from crowd movement in background\nSolution: Developed custom ML model trained on exhibition environment data, 95% accuracy',
      performance_optimization: '- Load balancing across 8 compute nodes\n- Redis caching reduced database queries by 80%\n- Asset preloading and progressive downloads\n- Error handling with graceful degradation\n- Real-time monitoring with Grafana dashboards',
      security_privacy: '- End-to-end encryption for all data transmission\n- GDPR-compliant data collection with explicit consent\n- Facial recognition data deleted after 24 hours\n- Anonymized analytics (no PII stored)\n- Regular security audits and penetration testing\n- SSL/TLS certificates for all connections',
      
      visitor_engagement_metrics: 'Total Interactions: 12,847\nUnique Visitors: 8,392\nAverage Dwell Time: 4 minutes 32 seconds (industry avg: 45 seconds)\nCompletion Rate: 67% of visitors completed full experience\nReturn Visitors: 23% came back for second interaction\nPeak Concurrent Users: 43 (system capacity: 50)',
      technology_performance: 'System Uptime: 99.7% (5 days, 23 hours of 6-day event)\nAverage Response Time: 47ms\nPeak Load Handled: 43 simultaneous users (86% of capacity)\nError Rate: 0.3%\nGesture Recognition Accuracy: 94.7%\nVoice Command Success Rate: 91.2%\nNetwork Bandwidth Used: Peak 2.3Gbps, Average 850Mbps',
      business_impact: 'Leads Generated: 2,847 (340% increase vs previous year)\nQualified Leads: 1,234 (43% qualification rate)\nOn-site Sales: €127,000\nPost-event Conversions: €340,000 (tracked via CRM)\nCost per Lead: €42 (industry avg: €120)\nSocial Media Mentions: 1,247 with 4.2M impressions\nPress Coverage: 18 publications, est. value €85,000',
      
      demo_video_primary: 'https://youtube.com/watch?v=dQw4w9WgXcQ',
      demo_video_short: 'https://vimeo.com/123456789',
      user_testing_video: 'https://youtube.com/watch?v=testing123',
      virtual_demo_url: 'https://demo.example.com/interactive-experience',
      
      development_team: 'Lead Developer: Alex Chen - Unity development & AR implementation\nBackend Engineer: Sarah Johnson - API development & database design\nML Engineer: Dr. Raj Patel - Computer vision & gesture recognition\nUX Designer: Emma Schmidt - Interface design & user testing\nDevOps: Mike Rodriguez - Infrastructure & deployment\nQA Engineer: Lisa Wang - Testing & quality assurance',
      tech_partners: 'Hardware:\n- Dell Technologies - Computing infrastructure\n- Samsung - Display panels\n- Microsoft - Azure Kinect sensors\n\nSoftware:\n- Unity Technologies - AR framework license\n- OpenAI - GPT-4 API access\n- Microsoft Azure - Cloud hosting\n\nIntegration Partners:\n- Salesforce - CRM integration\n- Google Cloud - Analytics platform',
      
      energy_efficiency: '- Idle mode reduces power consumption by 60%\n- Energy-efficient LED displays (30% less than standard)\n- Cloud computing offloading reduces local power needs\n- Total power consumption: 8.2 kW (vs 15 kW for traditional setup)\n- Carbon offset: 2.4 tons CO2 through renewable energy credits',
      scalability_reusability: 'Modular architecture allows:\n- Scaling from 5 to 50 concurrent users by adding compute nodes\n- Reusable software platform for multiple brands/products\n- Cloud-based content management for easy updates\n- Hardware components designed for transport and reassembly\n- Successfully deployed at 3 subsequent events with minimal modifications',
      
      awards_recognition: '- Fast Company Innovation by Design Award 2024\n- Webby Awards - Best Use of AR/VR\n- Featured in TechCrunch, The Verge, WIRED\n- Case study published in MIT Technology Review',
      tech_publications: 'https://medium.com/our-tech-blog/ar-exhibition-case-study\nhttps://developers.unity.com/case-studies/our-project',
      future_developments: 'Planned enhancements:\n- Integration with Apple Vision Pro for spatial computing\n- Real-time language translation (15+ languages)\n- Multiplayer collaborative experiences\n- AI-generated personalized content\n- Blockchain-based digital collectibles for visitors'
    };

    setFormData(prev => ({
      ...prev,
      categorySpecificData: {
        ...prev.categorySpecificData,
        [categoryId]: testData
      }
    }));

    toast.success('Technology test data filled! Note: Photo fields need manual upload.');
  };

  // Validation check for preview
  const validateForm = () => {
    const errors = [];

    // Step 1: Category
    if (formData.selectedCategories.length === 0) {
      errors.push('Please select a category');
    }

    // Step 2: Basic Info
    if (!formData.firstName) errors.push('First name is required');
    if (!formData.lastName) errors.push('Last name is required');
    if (!formData.email) errors.push('Email is required');
    if (!formData.phone) errors.push('Phone is required');
    if (!formData.company) errors.push('Company is required');
    if (!formData.country) errors.push('Country is required');

    // Step 3: Category-specific required fields
    if (formData.selectedCategories.length > 0) {
      const categoryId = formData.selectedCategories[0];
      const category = CATEGORIES.find(c => c.id === categoryId);
      const data = formData.categorySpecificData[categoryId] || {};

      if (category) {
        category.fields.forEach(field => {
          // Skip section dividers
          if (field.type === 'section-divider') return;
          
          if (field.required && field.type !== 'info') {
            // For photo fields, check if array has at least one item
            if (field.type === 'photos') {
              const photos = data[field.name] || [];
              if (!Array.isArray(photos) || photos.length === 0) {
                errors.push(`${field.label} requires at least 1 photo`);
              }
            } else if (!data[field.name] || (typeof data[field.name] === 'string' && data[field.name].trim() === '')) {
              errors.push(`${field.label} is required`);
            }
          }
        });
      }
    }

    return errors;
  };

  const handlePreview = () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      toast.error(`Please complete all required fields:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? `\n...and ${errors.length - 3} more` : ''}`);
      return;
    }

    setShowPreview(true);
  };

  // Save as draft (doesn't submit)
  const handleSaveDraft = async () => {
    setIsSaving(true);
    
    try {
      // Prepare submission data
      const category = CATEGORIES.find(c => c.id === formData.selectedCategories[0]);
      const basicInfo = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        country: formData.country
      };
      
      const categorySpecificData = formData.categorySpecificData[formData.selectedCategories[0]] || {};
      
      // Convert ALL File objects to base64 strings (photos + PDFs + other files)
      const processedData = { ...categorySpecificData };
      
      // Helper function to convert File to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve) => {
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.readAsDataURL(file);
          } else {
            resolve(file); // Already a string or null
          }
        });
      };
      
      // Process all fields
      const conversionPromises = [];
      for (const [key, value] of Object.entries(processedData)) {
        if (value instanceof File) {
          // Single file field
          conversionPromises.push(
            fileToBase64(value).then(base64 => {
              processedData[key] = base64;
            })
          );
        } else if (Array.isArray(value) && value.length > 0) {
          // Array of files (photos)
          conversionPromises.push(
            Promise.all(value.map(item => fileToBase64(item))).then(converted => {
              processedData[key] = converted;
            })
          );
        }
      }
      
      // Wait for all conversions to complete
      await Promise.all(conversionPromises);
      
      // Save to backend (status remains draft)
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = isEditMode 
        ? `${backendUrl}/api/submissions/${editId}` 
        : `${backendUrl}/api/submissions`;
      const method = isEditMode ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          category: category.title,
          basic_info: basicInfo,
          category_specific_data: processedData,
          status: 'draft' // Save as draft
        })
      });
      
      if (response.ok) {
        toast.success('Draft saved successfully!');
        setIsSaving(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save draft');
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Connection error. Please try again.');
      setIsSaving(false);
    }
  };

  // Submit application (final submission)
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Prepare submission data
      const category = CATEGORIES.find(c => c.id === formData.selectedCategories[0]);
      const basicInfo = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        country: formData.country
      };
      
      const categorySpecificData = formData.categorySpecificData[formData.selectedCategories[0]] || {};
      
      // Convert ALL File objects to base64 strings (photos + PDFs + other files)
      const processedData = { ...categorySpecificData };
      
      // Helper function to convert File to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve) => {
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.readAsDataURL(file);
          } else {
            resolve(file); // Already a string or null
          }
        });
      };
      
      // Process all fields
      const conversionPromises = [];
      for (const [key, value] of Object.entries(processedData)) {
        if (value instanceof File) {
          // Single file field
          conversionPromises.push(
            fileToBase64(value).then(base64 => {
              processedData[key] = base64;
            })
          );
        } else if (Array.isArray(value) && value.length > 0) {
          // Array of files (photos)
          conversionPromises.push(
            Promise.all(value.map(item => fileToBase64(item))).then(converted => {
              processedData[key] = converted;
            })
          );
        }
      }
      
      // Wait for all conversions to complete
      await Promise.all(conversionPromises);
      
      // Submit to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = isEditMode 
        ? `${backendUrl}/api/submissions/${editId}` 
        : `${backendUrl}/api/submissions`;
      const method = isEditMode ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          category: category.title,
          basic_info: basicInfo,
          category_specific_data: processedData,
          status: 'submitted' // Submit as final
        })
      });
      
      if (response.ok) {
        // Success!
        toast.success('Application submitted successfully!');
        setSubmitting(false);
        
        // Special handling for Judge's Choice - show success page
        if (formData.selectedCategories[0] === 'judges_choice') {
          // Find the selected project name
          const selectedProjectId = formData.categorySpecificData?.judges_choice?.selected_project_id;
          const selectedProject = userSubmissions.find(s => s.id === selectedProjectId);
          const projectName = selectedProject?.category_specific_data?.project_name || 
                            selectedProject?.project_details?.project_name ||
                            `Untitled - ${selectedProject?.category}`;
          setSelectedProjectName(projectName);
          setJudgesChoiceSuccess(true);
        } else {
          navigate('/dashboard');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || (isEditMode ? 'Failed to update application' : 'Failed to submit application'));
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Connection error. Please try again.');
      setSubmitting(false);
    }
  };

  const isStep1Valid = formData.selectedCategories.length > 0;
  const isStep2Valid = formData.firstName && formData.lastName && formData.email && formData.phone && formData.company && formData.country;

  // Calculate form completion percentage
  const calculateCompletion = () => {
    if (formData.selectedCategories.length === 0) return 0;
    
    const categoryId = formData.selectedCategories[0];
    const category = CATEGORIES.find(c => c.id === categoryId);
    if (!category) return 0;
    
    const data = formData.categorySpecificData[categoryId] || {};
    const requiredFields = category.fields.filter(f => f.required && f.type !== 'section-divider' && f.type !== 'info');
    const filledFields = requiredFields.filter(field => {
      if (field.type === 'photos') {
        const photos = data[field.name] || [];
        return Array.isArray(photos) && photos.length > 0;
      }
      return data[field.name] && (typeof data[field.name] !== 'string' || data[field.name].trim() !== '');
    });
    
    // Add basic info fields to count (6 required fields)
    const totalRequired = requiredFields.length + 6;
    const totalFilled = filledFields.length + (isStep2Valid ? 6 : 0);
    
    return Math.round((totalFilled / totalRequired) * 100);
  };

  const completionPercentage = calculateCompletion();

  // Calculate submission limits
  const calculateLimits = () => {
    const totalSubmissions = userSubmissions.filter(s => s.id !== editId).length; // Exclude current if editing
    const selectedCategoryId = formData.selectedCategories[0];
    
    if (!selectedCategoryId) {
      return { categoryCount: 0, categoryLimit: 3, totalCount: totalSubmissions, totalLimit: 10 };
    }
    
    const selectedCategory = CATEGORIES.find(c => c.id === selectedCategoryId);
    const categorySubmissions = userSubmissions.filter(s => {
      if (s.id === editId) return false; // Exclude current if editing
      const submissionCategory = CATEGORIES.find(c => c.title === s.category);
      return submissionCategory?.id === selectedCategoryId;
    });
    
    return {
      categoryCount: categorySubmissions.length,
      categoryLimit: 3,
      categoryName: selectedCategory?.title || '',
      totalCount: totalSubmissions,
      totalLimit: 10
    };
  };

  const limits = calculateLimits();

  // Show loading state while loading submission
  if (loadingSubmission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-orange-500" />
          <p className="text-gray-600">Başvuru yükleniyor...</p>
        </div>
      </div>
    );
  }

  const selectedCategory = formData.selectedCategories.length > 0 
    ? CATEGORIES.find(c => c.id === formData.selectedCategories[0])
    : null;
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {isEditMode ? 'Başvuruyu Düzenle' : 'Yeni Başvuru Oluştur'}
          </h1>
          <p className="text-lg text-gray-600">
            {isEditMode ? 'Başvurunuzu güncelleyin' : `Adım ${step} / 3`}
          </p>
        </div>

        {/* Autosave Indicator */}
        {!isEditMode && step === 3 && (
          <div className="mb-4 flex items-center justify-center gap-2 text-sm text-gray-600">
            {isSaving ? (
              <>
                <Save className="h-4 w-4 animate-pulse text-orange-500" />
                <span>Saving...</span>
              </>
            ) : lastSaved ? (
              <>
                <Clock className="h-4 w-4 text-green-600" />
                <span>Last saved: {new Date(lastSaved).toLocaleTimeString()}</span>
              </>
            ) : null}
          </div>
        )}

        {/* Progress Indicator */}
        {step === 3 && formData.selectedCategories.length > 0 && (
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Form Completion</span>
              <span className={`text-sm font-bold ${
                completionPercentage >= 100 ? 'text-green-600' : 
                completionPercentage >= 75 ? 'text-orange-500' : 
                'text-gray-600'
              }`}>
                {completionPercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  completionPercentage >= 100 ? 'bg-green-600' : 
                  completionPercentage >= 75 ? 'bg-orange-500' : 
                  completionPercentage >= 50 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`h-2 w-20 rounded-full transition-colors ${
                    s <= step ? 'bg-amber-500' : 'bg-gray-300'
                  }`}
                />
                {s < 3 && <ChevronRight className="h-4 w-4 text-gray-400" />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Category</span>
            <span>Basic Info</span>
            <span>Details</span>
          </div>
        </div>

        {/* Step 1: Category Selection */}
        {step === 1 && (
          <Card className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Award Category</h2>
            <p className="text-gray-600 mb-6">Select one category for your submission</p>

            {/* Submission Limits Info Banner */}
            {!isEditMode && formData.selectedCategories.length > 0 && !loadingLimits && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">Submission Limits</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>
                        <span className="font-medium">This category:</span>{' '}
                        {limits.categoryCount + 1} of {limits.categoryLimit} submissions{' '}
                        <span className="text-blue-600">
                          ({limits.categoryLimit - limits.categoryCount} remaining)
                        </span>
                      </p>
                      <p>
                        <span className="font-medium">Total submissions:</span>{' '}
                        {limits.totalCount + 1} of {limits.totalLimit}{' '}
                        <span className="text-blue-600">
                          ({limits.totalLimit - limits.totalCount} remaining)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryToggle(category.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.selectedCategories.includes(category.id)
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      formData.selectedCategories.includes(category.id)
                        ? 'bg-amber-500'
                        : 'bg-gray-200'
                    }`}>
                      <category.icon className={`h-5 w-5 ${
                        formData.selectedCategories.includes(category.id)
                          ? 'text-white'
                          : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{category.title}</h3>
                      {formData.selectedCategories.includes(category.id) && (
                        <CheckCircle className="h-5 w-5 text-amber-500 mt-1" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!isStep1Valid}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="next-step-1"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Basic Information */}
        {step === 2 && (
          <Card className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john.doe@company.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 234 567 8900"
                  required
                />
              </div>

              <Separator className="my-6" />

              <div>
                <Label htmlFor="company">Company Name *</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Your Company Ltd."
                  required
                />
              </div>

              <div>
                <Label htmlFor="country">Country *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => handleInputChange('country', value)}
                  required
                >
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                data-testid="back-step-2"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!isStep2Valid}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="next-step-2"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Category-Specific Fields */}
        {step === 3 && (
          <div className="space-y-6">
            {formData.selectedCategories.map((categoryId) => {
              const category = CATEGORIES.find(c => c.id === categoryId);
              if (!category) return null;

              // Special UI for Judge's Choice Award
              if (categoryId === 'judges_choice') {
                // Get user's existing submissions (exclude Judge's Choice)
                const eligibleProjects = userSubmissions.filter(s => {
                  const submissionCategory = CATEGORIES.find(c => c.title === s.category);
                  return submissionCategory?.id !== 'judges_choice';
                });

                return (
                  <Card key={categoryId} className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-12 w-12 rounded-xl bg-amber-500 flex items-center justify-center">
                        <category.icon className="h-6 w-6 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
                    </div>

                    {/* Info Message */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Trophy className="h-8 w-8 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-blue-900 mb-2">Select Your Project</h3>
                          <p className="text-blue-800 text-sm leading-relaxed">
                            Choose one of your previously submitted projects to be considered for the Judge's Choice Award. 
                            The jury will evaluate the selected project for this special recognition.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Project Selection */}
                    {eligibleProjects.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-gray-600 mb-2">No eligible projects found</p>
                        <p className="text-sm text-gray-500">You need to submit a project in another category first before applying for Judge's Choice Award.</p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-6">
                          {eligibleProjects.map((project) => {
                            const projectCategory = CATEGORIES.find(c => c.title === project.category);
                            const projectName = project.category_specific_data?.project_name || 
                                              project.project_details?.project_name ||
                                              `Untitled - ${project.category}`;
                            
                            return (
                              <div
                                key={project.id}
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    categorySpecificData: {
                                      ...prev.categorySpecificData,
                                      judges_choice: { selected_project_id: project.id }
                                    }
                                  }));
                                }}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  formData.categorySpecificData?.judges_choice?.selected_project_id === project.id
                                    ? 'border-amber-500 bg-amber-50'
                                    : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`h-4 w-4 rounded-full border-2 ${
                                    formData.categorySpecificData?.judges_choice?.selected_project_id === project.id
                                      ? 'border-amber-500 bg-amber-500'
                                      : 'border-gray-300'
                                  }`}>
                                    {formData.categorySpecificData?.judges_choice?.selected_project_id === project.id && (
                                      <div className="h-full w-full flex items-center justify-center">
                                        <div className="h-2 w-2 bg-white rounded-full"></div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900">{projectName}</h4>
                                    <p className="text-sm text-gray-600">{project.category}</p>
                                  </div>
                                  {projectCategory && (
                                    <div className="flex-shrink-0">
                                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                        <projectCategory.icon className="h-5 w-5 text-amber-600" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-center">
                          <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || !formData.categorySpecificData?.judges_choice?.selected_project_id}
                            className="bg-amber-500 hover:bg-amber-600 px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="submit-judges-choice"
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-5 w-5 mr-2" />
                                Submit Selected Project
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </Card>
                );
              }

              return (
                <Card key={categoryId} className="p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-amber-500 flex items-center justify-center">
                        <category.icon className="h-6 w-6 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
                    </div>
                    
                    {/* Test Data Button - For Innovation and Technology Categories */}
                    {categoryId === 'innovation' && !isEditMode && (
                      <Button
                        type="button"
                        onClick={fillTestData}
                        variant="outline"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        data-testid="fill-test-data-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Fill Test Data
                      </Button>
                    )}
                    {categoryId === 'technology' && !isEditMode && (
                      <Button
                        type="button"
                        onClick={fillTechnologyTestData}
                        variant="outline"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        data-testid="fill-tech-test-data-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Fill Test Data
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {category.fields.map((field) => {
                      // Handle conditional field visibility
                      if (field.conditional) {
                        const conditionFieldValue = formData.categorySpecificData[categoryId]?.[field.conditional.field];
                        if (conditionFieldValue !== field.conditional.value) {
                          return null; // Don't render if condition not met
                        }
                      }
                      
                      if (field.type === 'section-divider') {
                        return (
                          <div key={field.name} className="pt-6 pb-2">
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">
                              {field.label}
                            </h3>
                            <div className="h-px bg-gradient-to-r from-orange-500 via-orange-300 to-transparent"></div>
                          </div>
                        );
                      }

                      if (field.type === 'info') {
                        return (
                          <div key={field.name} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-900">{field.value}</p>
                          </div>
                        );
                      }

                      if (field.type === 'select') {
                        const value = formData.categorySpecificData[categoryId]?.[field.name] || '';
                        return (
                          <div key={field.name}>
                            <Label htmlFor={`${categoryId}-${field.name}`}>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <Select 
                              value={value} 
                              onValueChange={(val) => handleCategorySpecificChange(categoryId, field.name, val)}
                            >
                              <SelectTrigger id={`${categoryId}-${field.name}`}>
                                <SelectValue placeholder="Select an option" />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }

                      if (field.type === 'checkbox-group') {
                        const selectedValues = formData.categorySpecificData[categoryId]?.[field.name] || [];
                        return (
                          <div key={field.name}>
                            <Label>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <div className="space-y-3 mt-3">
                              {field.options.map((option) => (
                                <div key={option} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`${categoryId}-${field.name}-${option}`}
                                    checked={selectedValues.includes(option)}
                                    onCheckedChange={(checked) => {
                                      const newValues = checked
                                        ? [...selectedValues, option]
                                        : selectedValues.filter(v => v !== option);
                                      handleCategorySpecificChange(categoryId, field.name, newValues);
                                    }}
                                  />
                                  <label
                                    htmlFor={`${categoryId}-${field.name}-${option}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      if (field.type === 'number') {
                        const value = formData.categorySpecificData[categoryId]?.[field.name] || '';
                        return (
                          <div key={field.name}>
                            <Label htmlFor={`${categoryId}-${field.name}`}>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <Input
                              id={`${categoryId}-${field.name}`}
                              type="number"
                              min={field.min}
                              placeholder={field.placeholder || ''}
                              value={value}
                              onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.value)}
                              required={field.required}
                            />
                          </div>
                        );
                      }

                      if (field.type === 'textarea') {
                        const value = formData.categorySpecificData[categoryId]?.[field.name] || '';
                        const rows = field.rows || 4;
                        return (
                          <div key={field.name}>
                            <Label htmlFor={`${categoryId}-${field.name}`}>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <textarea
                              id={`${categoryId}-${field.name}`}
                              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              style={{ minHeight: `${rows * 30}px` }}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              value={value}
                              onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.value)}
                              required={field.required}
                              rows={rows}
                            />
                            {(field.maxWords || field.maxLength) && (
                              <WordCounter
                                value={value}
                                maxWords={field.maxWords}
                                maxChars={field.maxLength}
                              />
                            )}
                          </div>
                        );
                      }

                      if (field.type === 'file') {
                        const existingFile = formData.categorySpecificData[categoryId]?.[field.name];
                        const hasExistingFile = existingFile && (typeof existingFile === 'string' || existingFile instanceof File);
                        
                        // Get file name
                        let fileName = 'Yüklü dosya';
                        let fileSize = '';
                        if (existingFile instanceof File) {
                          fileName = existingFile.name;
                          fileSize = `${(existingFile.size / 1024).toFixed(1)} KB`;
                        } else if (typeof existingFile === 'string' && existingFile.length > 0) {
                          // Try to extract filename from base64 metadata
                          fileName = 'Yüklü dosya';
                          fileSize = `${(existingFile.length / 1024).toFixed(1)} KB`;
                        }
                        
                        return (
                          <div key={field.name}>
                            <Label htmlFor={`${categoryId}-${field.name}`}>
                              {field.label} {field.required && '*'}
                            </Label>
                            
                            {hasExistingFile && (
                              <div className="mt-2 mb-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-green-900">{fileName}</p>
                                      {fileSize && (
                                        <p className="text-xs text-green-600">{fileSize}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    {typeof existingFile === 'string' && existingFile.startsWith('data:') && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = existingFile;
                                          link.download = fileName;
                                          link.click();
                                        }}
                                        className="text-green-700 border-green-300 hover:bg-green-100"
                                      >
                                        İndir
                                      </Button>
                                    )}
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleCategorySpecificChange(categoryId, field.name, null)}
                                      className="text-red-700 border-red-300 hover:bg-red-50"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {!hasExistingFile && (
                              <div className="mt-1 flex items-center gap-2">
                                <Input
                                  id={`${categoryId}-${field.name}`}
                                  type="file"
                                  className="flex-1"
                                  onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.files[0])}
                                  required={field.required}
                                />
                                <Upload className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                            
                            {hasExistingFile && (
                              <div className="mt-2">
                                <Label htmlFor={`${categoryId}-${field.name}-replace`} className="text-xs text-gray-600">
                                  Yeni dosya yükle (opsiyonel)
                                </Label>
                                <div className="mt-1 flex items-center gap-2">
                                  <Input
                                    id={`${categoryId}-${field.name}-replace`}
                                    type="file"
                                    className="flex-1"
                                    onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.files[0])}
                                  />
                                  <Upload className="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      if (field.type === 'photos') {
                        const photos = formData.categorySpecificData[categoryId]?.[field.name] || [];
                        // Use enhanced upload for innovation and technology categories
                        const useEnhanced = categoryId === 'innovation' || categoryId === 'technology';
                        return (
                          <div key={field.name}>
                            <Label>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-sm text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <div className="mt-2">
                              {useEnhanced ? (
                                <PhotoUploadEnhanced
                                  photos={photos}
                                  onChange={(newPhotos) => handleCategorySpecificChange(categoryId, field.name, newPhotos)}
                                  maxPhotos={field.maxFiles || 5}
                                  acceptPDF={field.acceptPDF || false}
                                  fieldId={`${categoryId}-${field.name}`}
                                  enableReorder={true}
                                  enableCamera={true}
                                />
                              ) : (
                                <PhotoUpload
                                  photos={photos}
                                  onChange={(newPhotos) => handleCategorySpecificChange(categoryId, field.name, newPhotos)}
                                  maxPhotos={field.maxFiles || 5}
                                  acceptPDF={field.acceptPDF || false}
                                  fieldId={`${categoryId}-${field.name}`}
                                />
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (field.type === 'url') {
                        const value = formData.categorySpecificData[categoryId]?.[field.name] || '';
                        return (
                          <div key={field.name}>
                            <Label htmlFor={`${categoryId}-${field.name}`}>
                              {field.label} {field.required && '*'}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                            )}
                            <Input
                              id={`${categoryId}-${field.name}`}
                              type="url"
                              placeholder={field.placeholder || 'https://...'}
                              value={value}
                              onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.value)}
                              required={field.required}
                            />
                          </div>
                        );
                      }

                      const value = formData.categorySpecificData[categoryId]?.[field.name] || '';
                      return (
                        <div key={field.name}>
                          <Label htmlFor={`${categoryId}-${field.name}`}>
                            {field.label} {field.required && '*'}
                          </Label>
                          {field.description && (
                            <p className="text-xs text-gray-600 mt-1 mb-2">{field.description}</p>
                          )}
                          <Input
                            id={`${categoryId}-${field.name}`}
                            type={field.type}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            maxLength={field.maxLength}
                            value={value}
                            onChange={(e) => handleCategorySpecificChange(categoryId, field.name, e.target.value)}
                            required={field.required}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}

            {/* Hide action buttons for Judge's Choice (already in special UI above) */}
            {formData.selectedCategories[0] !== 'judges_choice' && (
              <Card className="p-6 sm:p-8 bg-amber-50 border-amber-200">
                <h3 className="font-bold text-gray-900 mb-2">{isEditMode ? 'Değişiklikleri Kaydet' : 'Başvuruyu Tamamla'}</h3>
              <p className="text-sm text-gray-700 mb-4">
                {isEditMode 
                  ? 'Değişikliklerinizi kaydetmek veya vazgeçmek için aşağıdaki butonları kullanın.' 
                  : 'Lütfen göndermeden önce tüm bilgileri kontrol edin. Kısa süre içinde onay e-postası alacaksınız.'}
              </p>
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setStep(2)}
                    variant="outline"
                    data-testid="back-step-3"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Geri
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="cancel-application"
                  >
                    Vazgeç
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handlePreview}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="preview-application"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    onClick={handleSaveDraft}
                    disabled={isSaving || submitting}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="save-application"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Draft
                      </>
                    )}
                  </Button>
                  {!isEditMode && (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="submit-application"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application <CheckCircle className="h-4 w-4 ml-1" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <SubmissionPreview
        formData={formData}
        category={selectedCategory}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onEdit={() => setShowPreview(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  );
};